"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { X, Atom } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/ui/components/ui/button";
import type { StackCombination, BlendMatch, PeptideProfile } from "@/config/peptide-stacks";
import { SynergyScore } from "./synergy-score";
import { VariantPicker } from "./variant-picker";
import { BlendHint } from "./blend-hint";
import { ShareButtons } from "./share-buttons";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariantChoice {
	variantId: string;
	quantity: number;
}

interface SelectedProductEntry {
	slug: string;
	product: unknown;
	profile: PeptideProfile | undefined;
	variant: VariantChoice | null;
}

interface StackAnalysisProps {
	selectedProducts: SelectedProductEntry[];
	synergies: StackCombination[];
	aggregateScore: number | null;
	blendMatch: BlendMatch | null;
	blendProduct: unknown;
	totalPrice: number;
	formattedTotal: string;
	totalPresentation: ResolvedMoneyPresentation | null;
	currency: string;
	locale: string;
	channel: string;
	goal?: string | null;
	onRemove: (slug: string) => void;
	onVariantChange: (slug: string, variantId: string) => void;
	onQuantityChange: (slug: string, quantity: number) => void;
	/** Resolves `true` only when every line landed in the checkout. */
	onAddToCart: () => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ProductRecord = Record<string, unknown>;

function getProductName(product: unknown): string | null {
	const p = product as ProductRecord | null;
	if (!p) return null;
	return (p.name as string) ?? null;
}

function getProductVariants(product: unknown): unknown[] {
	const p = product as ProductRecord | null;
	if (!p) return [];
	const variants = p.variants;
	if (!Array.isArray(variants)) return [];
	return variants;
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ onGuidedClick }: { onGuidedClick?: () => void }) {
	const t = useTranslations("stackBuilder");
	void onGuidedClick;

	return (
		<div className="flex flex-col items-center gap-4 py-10 text-center">
			<div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
				<Atom className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
			</div>
			<div className="space-y-1">
				<p className="text-sm font-medium text-foreground">{t("analysis.empty")}</p>
				<p className="text-xs text-muted-foreground">
					{t("analysis.emptyGuided")}{" "}
					<Link href="?mode=guided" className="font-semibold text-accent underline-offset-2 hover:underline">
						{t("guidedMode")}
					</Link>
				</p>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Synergy pair row
// ---------------------------------------------------------------------------

interface SynergyPairRowProps {
	synergy: StackCombination;
}

function SynergyPairRow({ synergy }: SynergyPairRowProps) {
	const t = useTranslations("stackBuilder");

	const rationaleKey = `combinations.${synergy.rationaleKey}.rationale` as Parameters<typeof t>[0];
	const timingKey = `combinations.${synergy.timingAdviceKey}.timing` as Parameters<typeof t>[0];
	const warningKey = synergy.overlapWarningKey
		? (`combinations.${synergy.overlapWarningKey}.warning` as Parameters<typeof t>[0])
		: null;

	// New-peptide combos ship with English source strings on the StackCombination and are
	// translated per-locale on demand; fall back to the source string when a locale lacks the key.
	const rationale = t.has(rationaleKey) ? t(rationaleKey) : synergy.rationale;
	const timing = t.has(timingKey) ? t(timingKey) : synergy.timingAdvice;
	const warning = warningKey ? (t.has(warningKey) ? t(warningKey) : (synergy.overlapWarning ?? null)) : null;
	const hasWarning = warning && warning.trim().length > 0;

	const pairLabel = synergy.peptides.join(" + ");

	return (
		<div className="space-y-2">
			{/* Pair header */}
			<div className="flex items-center justify-between gap-2">
				<span className="truncate text-xs font-semibold uppercase tracking-wide text-foreground">
					{pairLabel}
				</span>
				<SynergyScore score={synergy.synergyScore} size="sm" />
			</div>

			{/* Rationale */}
			<p className="text-[11px] leading-relaxed text-muted-foreground">{rationale}</p>

			{/* Timing */}
			<div className="rounded-md bg-muted px-2.5 py-1.5">
				<p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
					{t("analysis.timing")}
				</p>
				<p className="mt-0.5 text-[11px] text-foreground">{timing}</p>
			</div>

			{/* Overlap warning */}
			{hasWarning && (
				<div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 dark:border-amber-900 dark:bg-amber-950/30">
					<span className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true">
						⚠
					</span>
					<div>
						<p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
							{t("analysis.overlapWarning")}
						</p>
						<p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-300">{warning}</p>
					</div>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function StackAnalysis({
	selectedProducts,
	synergies,
	aggregateScore,
	blendMatch,
	blendProduct,
	totalPrice,
	formattedTotal,
	totalPresentation,
	currency,
	locale,
	channel,
	goal,
	onRemove,
	onVariantChange,
	onQuantityChange,
	onAddToCart,
}: StackAnalysisProps) {
	const t = useTranslations("stackBuilder");

	const isEmpty = selectedProducts.length === 0;

	const [isAdding, setIsAdding] = useState(false);
	const [added, setAdded] = useState(false);
	const [failed, setFailed] = useState(false);

	async function handleAddToCartClick() {
		setIsAdding(true);
		setFailed(false);
		try {
			const ok = await onAddToCart();
			if (ok) {
				setAdded(true);
				setTimeout(() => setAdded(false), 1500);
			} else {
				setFailed(true);
				setTimeout(() => setFailed(false), 3000);
			}
		} catch {
			setFailed(true);
			setTimeout(() => setFailed(false), 3000);
		} finally {
			setIsAdding(false);
		}
	}

	return (
		<div
			className={cn("flex flex-col gap-4 rounded-xl border border-border bg-card p-4 shadow-sm", "text-sm")}
		>
			{/* ---- Header ---- */}
			<div className="flex items-center justify-between">
				<h2 className="text-base font-bold text-foreground">{t("analysis.yourStack")}</h2>
				{!isEmpty && (
					<span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-accent-foreground">
						{selectedProducts.length}
					</span>
				)}
			</div>

			{/* ---- Empty state ---- */}
			{isEmpty && <EmptyState />}

			{/* ---- Selected peptides list ---- */}
			{!isEmpty && (
				<div className="flex flex-col gap-3 divide-y divide-border">
					{selectedProducts.map(({ slug, product, profile, variant }) => {
						const name = getProductName(product) ?? profile?.name ?? slug;
						const variants = getProductVariants(product);

						return (
							<div key={slug} className="flex flex-col gap-2 pt-3 first:pt-0">
								{/* Row header: name + remove */}
								<div className="flex items-start justify-between gap-2">
									<p className="truncate text-sm font-semibold text-foreground">{name}</p>
									<button
										type="button"
										onClick={() => onRemove(slug)}
										className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
										aria-label={`Remove ${name} from stack`}
									>
										<X className="h-3.5 w-3.5" aria-hidden="true" />
									</button>
								</div>

								{/* Variant picker */}
								{variant && variants.length > 0 && (
									<VariantPicker
										variants={variants}
										selectedVariantId={variant.variantId}
										quantity={variant.quantity}
										onVariantChange={(variantId) => onVariantChange(slug, variantId)}
										onQuantityChange={(qty) => onQuantityChange(slug, qty)}
										channel={channel}
										locale={locale}
										currency={currency}
									/>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* ---- Synergy section (2+ peptides) ---- */}
			{synergies.length > 0 && (
				<div className="flex flex-col gap-4 border-t border-border pt-4">
					{/* Aggregate score + label */}
					{aggregateScore !== null && (
						<div className="flex items-center gap-3">
							<SynergyScore score={aggregateScore} size="md" />
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									{t("analysis.synergy")}
								</p>
								<p className="text-xs text-muted-foreground">
									{t("analysis.pairsAnalyzed", { count: synergies.length })}
								</p>
							</div>
						</div>
					)}

					{/* Per-pair analysis */}
					<div className="flex flex-col gap-4 divide-y divide-border">
						{synergies.map((synergy) => (
							<div key={synergy.peptides.join("_")} className="pt-4 first:pt-0">
								<SynergyPairRow synergy={synergy} />
							</div>
						))}
					</div>
				</div>
			)}

			{/* ---- Blend hint ---- */}
			{blendMatch != null && blendProduct != null && (
				<BlendHint
					blendProduct={blendProduct}
					individualTotal={totalPrice}
					currency={currency}
					locale={locale}
					channel={channel}
				/>
			)}

			{/* ---- Total price ---- */}
			{!isEmpty && (
				<div className="flex items-center justify-between border-t border-border pt-3">
					<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
						{t("analysis.total")}
					</span>
					<span className="text-base font-bold tabular-nums text-foreground">
						{totalPresentation ? (
							<ReferencePrimaryPrice
								presentation={totalPresentation}
								locale={locale}
								channel={channel}
								size="lg"
								className="text-base font-bold text-foreground"
							/>
						) : (
							formattedTotal
						)}
					</span>
				</div>
			)}

			{/* ---- CTA buttons ---- */}
			{!isEmpty && (
				<div className="flex flex-col gap-2">
					{/* Primary: Add Stack to Cart */}
					<Button
						variant={failed ? "destructive" : "default"}
						size="default"
						className="w-full"
						disabled={selectedProducts.length === 0 || isAdding}
						onClick={() => void handleAddToCartClick()}
						aria-live="polite"
					>
						{isAdding ? (
							<span className="flex items-center gap-2">
								<span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
								{t("adding")}
							</span>
						) : failed ? (
							t("addToCartFailed")
						) : added ? (
							t("added")
						) : (
							t("addToCart")
						)}
					</Button>

					{/* Secondary: Share + Save as Image */}
					<ShareButtons
						selectedSlugs={selectedProducts.map(({ slug }) => slug)}
						goal={goal ?? undefined}
						locale={locale}
						channel={channel}
					/>
				</div>
			)}
		</div>
	);
}
